// client/src/pages/secure/CrmPage.js — the CRM: dashboard, contacts, pipeline,
// follow-ups, saved lists, import.
//
// One screen for both offices; anything state-specific arrives from /api/crm/meta
// (the jurisdiction profile), never from a test of the state here.
//
// Nothing on this page sends a message. Calls, texts and emails start from a contact's
// card, after Contact Compliance has said yes, as an ordinary tel:/sms:/mailto: link.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select, Textarea, Button, Notice, Empty, Loading,
    money, fmtDate, titleCase,
} from '../../components/secure/pm/PMKit';
import { AboutThisPage } from '../../components/shared/PageHeader';

// ---------------------------------------------------------------- shared bits
export const STAGE_TONE = {
    first_contact: 'slate', appointment: 'indigo', agreement_signed: 'violet',
    under_contract: 'warn', closed: 'good', lost: 'bad',
};
export const DNC_PILL = {
    clear: ['clear', 'good'], partial: ['partly opted out', 'warn'], do_not_contact: ['do not contact', 'bad'],
    national_registry: ['national DNC', 'warn'], unknown: ['status unknown', 'slate'],
};
export const TouchPill = ({ touch }) => {
    if (!touch || !touch.cadence_days) return <span className="text-xs text-slate-400">no cadence</span>;
    if (touch.overdue) return <Pill tone="bad">touch due {fmtDate(touch.due_on)}</Pill>;
    return <span className="text-xs text-slate-500">next by {fmtDate(touch.due_on)}</span>;
};
export const nameOf = (c) => c.display_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || `#${c.contact_id}`;
const daysAgo = (d) => (d ? Math.round((Date.now() - new Date(d).getTime()) / 86400000) : null);

const useMeta = () => {
    const api = useApiClient();
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(() => api.get('/api/crm/meta').then(setMeta).catch((e) => setError(e.message)), [api]);
    useEffect(() => { load(); }, [load]);
    return { meta, error, reload: load };
};

const useAgents = (enabled) => {
    const api = useApiClient();
    const [agents, setAgents] = useState([]);
    useEffect(() => {
        if (!enabled) return;
        api.get('/api/agents').then((rows) => setAgents((rows || []).filter((a) => String(a.status || '').toLowerCase() !== 'terminated')))
            .catch(() => setAgents([]));
    }, [api, enabled]);
    return agents;
};

// ---------------------------------------------------------------- add contact
export const ContactForm = ({ meta, agents, initial = {}, linked = false, onSave, onCancel, busy }) => {
    const [f, setF] = useState({
        contact_kind: 'person', first_name: '', last_name: '', company_name: '', job_title: '', email: '', phone: '',
        alt_phone: '', street_address: '', city: '', state: '', zip_code: '', area: '', category: 'prospect', source: '',
        tags: '', preferred_channel: 'any', touch_every_days: '', notes: '', assigned_agent_id: '', ...initial,
    });
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const submit = (e) => { e.preventDefault(); onSave(f); };
    return (
        <form onSubmit={submit}>
            {linked && <Notice tone="info" message="This card is linked to a client record. Name, email, phone and address are edited on the client page, so the two never disagree." />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <Select label="Kind" value={f.contact_kind} onChange={set('contact_kind')} options={['person', 'company']} />
                <Select label="Relationship" value={f.category} onChange={set('category')} options={meta.categories} />
                {!linked && <Input label="First name" value={f.first_name || ''} onChange={set('first_name')} />}
                {!linked && <Input label="Last name" value={f.last_name || ''} onChange={set('last_name')} />}
                <Input label="Company" value={f.company_name || ''} onChange={set('company_name')} />
                <Input label="Job title" value={f.job_title || ''} onChange={set('job_title')} />
                {!linked && <Input label="Email" type="email" value={f.email || ''} onChange={set('email')} />}
                {!linked && <Input label="Phone" value={f.phone || ''} onChange={set('phone')} />}
                <Input label="Other phone" value={f.alt_phone || ''} onChange={set('alt_phone')} />
                <Select label="Prefers" value={f.preferred_channel} onChange={set('preferred_channel')} options={meta.channels} />
                {!linked && <Input label="Street address" value={f.street_address || ''} onChange={set('street_address')} />}
                {!linked && <Input label="City" value={f.city || ''} onChange={set('city')} />}
                {!linked && <Input label="State" value={f.state || ''} onChange={set('state')} />}
                {!linked && <Input label="ZIP" value={f.zip_code || ''} onChange={set('zip_code')} />}
                <Input label="Area / farm" value={f.area || ''} onChange={set('area')} hint="Neighbourhood or farm area, for area lists" />
                <Input label="Source" value={f.source || ''} onChange={set('source')} hint="Where they came from: open house, referral, website…" />
                <Input label="Tags" value={f.tags || ''} onChange={set('tags')} hint="Comma-separated" />
                <Input label="Keep in touch every (days)" type="number" min="1" value={f.touch_every_days ?? ''} onChange={set('touch_every_days')}
                    hint={`Blank = the ${titleCase(f.category)} default (${meta.settings?.cadence?.[f.category] ?? meta.default_cadence[f.category] ?? 'none'})`} />
                {agents.length > 0 && (
                    <Select label="Assigned agent" value={f.assigned_agent_id ?? ''} onChange={set('assigned_agent_id')}
                        options={[{ value: '', label: '— office pool —' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
                )}
            </div>
            <Textarea label="Notes" value={f.notes || ''} onChange={set('notes')} />
            <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
                <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </div>
        </form>
    );
};

// ---------------------------------------------------------------- dashboard
const DashboardTab = ({ meta, agents }) => {
    const api = useApiClient();
    const [days, setDays] = useState(90);
    const [agentId, setAgentId] = useState('');
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [note, setNote] = useState('');
    const load = useCallback(() => {
        api.get(`/api/crm/dashboard?days=${days}${agentId ? `&agent_id=${agentId}` : ''}`).then(setD).catch((e) => setError(e.message));
    }, [api, days, agentId]);
    useEffect(() => { load(); }, [load]);
    const bringIn = async () => {
        try { const r = await api.post('/api/crm/contacts/link-clients', {}); setNote(r.message); load(); } catch (e) { setError(e.message); }
    };
    if (error) return <Notice message={error} onDismiss={() => setError('')} />;
    if (!d) return <Loading />;
    const acts = d.activity.by_type;
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-3 items-end">
                <Select label="Window" value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-40"
                    options={[30, 90, 180, 365].map((n) => ({ value: n, label: `Last ${n} days` }))} />
                {meta.sees_everyone && (
                    <Select label="Agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="w-56"
                        options={[{ value: '', label: 'Whole office' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
                )}
            </div>
            {note && <Notice tone="good" message={note} onDismiss={() => setNote('')} />}
            {d.book.clients_without_card > 0 && (
                <Notice tone="info" title={`${d.book.clients_without_card} client(s) are not in the CRM yet`}
                    message="Bring them in to see them on lists and follow-ups. Nothing is copied: the card points at the client record." />
            )}
            {d.book.clients_without_card > 0 && <Button onClick={bringIn}>Bring clients into the CRM</Button>}
            {(d.activity.outbound_against_block > 0 || d.activity.outbound_unchecked > 0) && (
                <Notice tone="warn" title="Contact compliance"
                    problems={[
                        d.activity.outbound_against_block ? `${d.activity.outbound_against_block} outbound contact(s) logged after Contact Compliance had refused them.` : null,
                        d.activity.outbound_unchecked ? `${d.activity.outbound_unchecked} outbound contact(s) logged with no check run first.` : null,
                    ].filter(Boolean)} />
            )}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                <Stat label="Open pipeline" value={money(d.pipeline.value)} sub={`${d.pipeline.count} opportunities`} />
                <Stat label="Weighted" value={money(d.pipeline.weighted)} sub="by stage probability" />
                <Stat label="Weighted GCI" value={money(d.pipeline.gci)} sub="where a rate is entered" />
                <Stat label="Contacts" value={d.book.contacts} sub={`${d.book.added} added in window`} />
                <Stat label="Never touched" value={d.book.never_touched} tone={d.book.never_touched ? 'warn' : 'default'} />
                <Stat label="Touches" value={Object.values(acts).reduce((t, n) => t + n, 0)}
                    sub={['Call', 'Email', 'Text', 'Meeting'].map((k) => `${acts[k] || 0} ${k.toLowerCase()}`).join(' · ')} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Pipeline by stage" subtitle="Open stages plus anything that moved in the window">
                    <Table columns={[
                        { key: 'label', label: 'Stage', render: (r) => <Pill tone={STAGE_TONE[r.stage]}>{r.label}</Pill> },
                        { key: 'count', label: 'Count', align: 'right' },
                        { key: 'value', label: 'Value', align: 'right', render: (r) => money(r.value) },
                        { key: 'weighted', label: 'Weighted', align: 'right', render: (r) => money(r.weighted) },
                    ]} rows={d.by_stage} />
                </Panel>
                <Panel title="Conversion by stage" subtitle="Of the opportunities that reached a stage, how many reached the next">
                    <Table columns={[
                        { key: 'label', label: 'Stage' },
                        { key: 'reached', label: 'Reached', align: 'right' },
                        { key: 'rate', label: 'To next', align: 'right', render: (r) => (r.rate == null ? '—' : `${r.rate}%`) },
                    ]} rows={d.conversion} />
                </Panel>
                <Panel title="By source" subtitle="Win rate = closed ÷ (closed + lost)">
                    <Table columns={[
                        { key: 'label', label: 'Source' }, { key: 'total', label: 'Total', align: 'right' },
                        { key: 'open', label: 'Open', align: 'right' }, { key: 'won', label: 'Closed', align: 'right' },
                        { key: 'lost', label: 'Lost', align: 'right' },
                        { key: 'win_rate', label: 'Win rate', align: 'right', render: (r) => (r.win_rate == null ? '—' : `${r.win_rate}%`) },
                    ]} rows={d.by_source} empty="No opportunities yet." />
                </Panel>
                <Panel title="By agent">
                    <Table columns={[
                        { key: 'label', label: 'Agent' }, { key: 'open', label: 'Open', align: 'right' },
                        { key: 'open_value', label: 'Open value', align: 'right', render: (r) => money(r.open_value) },
                        { key: 'won', label: 'Closed', align: 'right' }, { key: 'lost', label: 'Lost', align: 'right' },
                        { key: 'win_rate', label: 'Win rate', align: 'right', render: (r) => (r.win_rate == null ? '—' : `${r.win_rate}%`) },
                    ]} rows={d.by_agent} empty="No opportunities yet." />
                </Panel>
                <Panel title="Activity by agent" subtitle={`Logged on CRM cards in the last ${d.days} days`}>
                    <Table columns={[
                        { key: 'agent_name', label: 'Agent' },
                        ...['Call', 'Email', 'Text', 'Meeting', 'Note'].map((k) => ({ key: k, label: k, align: 'right', render: (r) => r[k] || 0 })),
                        { key: 'total', label: 'Total', align: 'right' },
                    ]} rows={d.activity.by_agent} empty="Nothing logged in this window." />
                </Panel>
                <Panel title="Why opportunities were lost">
                    <Table columns={[{ key: 'label', label: 'Reason' }, { key: 'count', label: 'Count', align: 'right' }]}
                        rows={d.lost_reasons} empty="Nothing lost in this window." />
                </Panel>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------- contacts
const ContactsTab = ({ meta, agents, segments, reloadSegments, initialSegment }) => {
    const api = useApiClient();
    const navigate = useNavigate();
    const blank = { q: '', category: '', tag: '', source: '', area: '', agent_id: '', no_touch_days: '', stage: '', segment_id: initialSegment || '' };
    const [f, setF] = useState(blank);
    const [archived, setArchived] = useState(false);
    const [touchDue, setTouchDue] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [listName, setListName] = useState(null);
    const [msg, setMsg] = useState('');

    const qs = useMemo(() => {
        const p = new URLSearchParams();
        Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
        if (archived) p.set('archived', '1');
        if (touchDue) p.set('touch_due', '1');
        return p.toString();
    }, [f, archived, touchDue]);
    const load = useCallback(() => {
        setData(null);
        api.get(`/api/crm/contacts?${qs}`).then(setData).catch((e) => { setError(e.message); setData({ rows: [] }); });
    }, [api, qs]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
    useEffect(() => { if (initialSegment) setF((x) => ({ ...x, segment_id: initialSegment })); }, [initialSegment]);
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

    const create = async (body, allowDuplicate = false) => {
        setSaving(true); setError('');
        try {
            const r = await api.post('/api/crm/contacts', { ...body, allow_duplicate: allowDuplicate });
            setAdding(false);
            navigate(`/dashboard/crm/contacts/${r.contact_id}`);
        } catch (e) {
            if (e.status === 409 && e.payload?.duplicate_of) {
                if (window.confirm(`${e.message} Open the existing card instead?`)) navigate(`/dashboard/crm/contacts/${e.payload.duplicate_of}`);
            } else setError(e.message);
        } finally { setSaving(false); }
    };
    const saveList = async () => {
        const filters = {
            q: f.q || undefined, categories: f.category ? [f.category] : undefined, tags: f.tag ? f.tag.split(',') : undefined,
            sources: f.source ? [f.source] : undefined, areas: f.area ? [f.area] : undefined,
            agent_ids: f.agent_id ? [f.agent_id] : undefined, no_touch_days: f.no_touch_days || undefined,
            stages: f.stage ? [f.stage] : undefined,
        };
        try {
            await api.post('/api/crm/segments', { name: listName.name, is_shared: listName.shared, filters });
            setListName(null); setMsg('List saved. It re-runs every time you open it, so it is always current.'); reloadSegments();
        } catch (e) { setError(e.message); }
    };
    const exportCsv = async () => {
        try {
            const res = await fetch(`/api/crm/export?${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
            if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || `Export failed (${res.status})`); }
            const url = URL.createObjectURL(await res.blob());
            const a = document.createElement('a');
            a.href = url; a.download = `crm-contacts-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { setError(e.message); }
    };

    const rows = data?.rows || [];
    return (
        <div className="space-y-4">
            {error && <Notice message={error} onDismiss={() => setError('')} />}
            {msg && <Notice tone="good" message={msg} onDismiss={() => setMsg('')} />}
            <Panel>
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-x-3">
                    <Input label="Search" value={f.q} onChange={set('q')} placeholder="name, email, phone" className="col-span-2" />
                    <Select label="Saved list" value={f.segment_id} onChange={set('segment_id')}
                        options={[{ value: '', label: '—' }, ...segments.map((s) => ({ value: s.segment_id, label: s.name }))]} />
                    <Select label="Relationship" value={f.category} onChange={set('category')}
                        options={[{ value: '', label: 'Any' }, ...meta.categories.map((c) => ({ value: c, label: titleCase(c) }))]} />
                    <Input label="Tag" value={f.tag} onChange={set('tag')} />
                    <Input label="Source" value={f.source} onChange={set('source')} />
                    <Input label="Area / city / ZIP" value={f.area} onChange={set('area')} />
                    <Select label="Open stage" value={f.stage} onChange={set('stage')}
                        options={[{ value: '', label: 'Any' }, ...meta.stages.map((s) => ({ value: s.key, label: s.label }))]} />
                    <Input label="No touch in (days)" type="number" min="1" value={f.no_touch_days} onChange={set('no_touch_days')} />
                    {meta.sees_everyone && (
                        <Select label="Agent" value={f.agent_id} onChange={set('agent_id')}
                            options={[{ value: '', label: 'Anyone' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={touchDue} onChange={(e) => setTouchDue(e.target.checked)} />Keep-in-touch overdue only</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />Archived</label>
                    <div className="flex-1" />
                    <Button variant="ghost" onClick={() => setF(blank)}>Clear</Button>
                    <Button variant="ghost" onClick={() => setListName({ name: '', shared: false })}>Save as list</Button>
                    {meta.can_export && <Button variant="ghost" onClick={exportCsv}>Export CSV</Button>}
                    <Button onClick={() => setAdding(true)}>Add contact</Button>
                </div>
            </Panel>
            <Panel title={data ? `${rows.length}${data.truncated ? '+' : ''} contact(s)` : 'Contacts'}
                subtitle={data?.truncated ? 'Showing the first 500 — narrow the filter.' : undefined}>
                {!data ? <Loading /> : (
                    <DataList rows={rows} rowKey="contact_id" csvName="crm-contacts-shown" csv={!!meta.can_export}
                        onRowClick={(r) => navigate(`/dashboard/crm/contacts/${r.contact_id}`)}
                        rowClass={(r) => (r.touch?.overdue ? 'bg-amber-50' : '')}
                        defaultSort={{ key: 'last', dir: 'desc' }} empty="No contacts match."
                        note={`The box above asks the server; this one searches and sorts the ${rows.length} row${rows.length === 1 ? '' : 's'} loaded.${meta.can_export ? ' Export CSV (above) is the full export; CSV here is what is shown.' : ''}`}
                        columns={[
                            { key: 'name', label: 'Name', value: (r) => [nameOf(r), r.company_name].filter(Boolean).join(' · '), render: (r) => (
                                <div><div className="font-medium text-slate-800">{nameOf(r)}</div>
                                    {r.company_name && (r.first_name || r.last_name) && <div className="text-xs text-slate-500">{r.company_name}</div>}</div>) },
                            { key: 'category', label: 'Relationship', value: (r) => `${titleCase(r.category)}${r.client_id ? ' client' : ''}`, render: (r) => (
                                <span className="space-x-1"><Pill>{titleCase(r.category)}</Pill>{r.client_id && <Pill tone="indigo">client</Pill>}</span>) },
                            { key: 'contact', label: 'Reach', value: (r) => [r.email, r.phone].filter(Boolean).join(' '), render: (r) => <div className="text-xs">{r.email || ''}<br />{r.phone || ''}</div> },
                            { key: 'agent_name', label: 'Agent', value: (r) => r.agent_name || 'office', render: (r) => r.agent_name || <span className="text-slate-400">office</span> },
                            { key: 'last', label: 'Last touch', type: 'date', value: (r) => r.last_contacted_at || null,
                                render: (r) => <span className="whitespace-nowrap">{r.last_contacted_at ? `${daysAgo(r.last_contacted_at)}d ago` : <span className="text-slate-400">never</span>}</span> },
                            { key: 'touch', label: 'Keep in touch', value: (r) => (!r.touch?.cadence_days ? 'no cadence' : `${r.touch.overdue ? 'touch due' : 'next by'} ${fmtDate(r.touch.due_on)}`), sortValue: (r) => r.touch?.due_on || null, render: (r) => <TouchPill touch={r.touch} /> },
                            { key: 'dnc', label: 'Contact', value: (r) => (DNC_PILL[r.compliance?.summary] || DNC_PILL.unknown)[0], render: (r) => { const [l, t] = DNC_PILL[r.compliance?.summary] || DNC_PILL.unknown; return <Pill tone={t}>{l}</Pill>; } },
                            { key: 'open_opportunities', label: 'Open opps', type: 'number' },
                            ...(archived ? [{ key: 'retain_until', label: 'Kept until', type: 'date', render: (r) => fmtDate(r.retain_until) }] : []),
                        ]} />
                )}
            </Panel>
            {adding && (
                <Modal title="Add a contact" subtitle="Checked against the whole firm's book by email and phone before it is saved" onClose={() => setAdding(false)} wide>
                    <ContactForm meta={meta} agents={meta.sees_everyone ? agents : []} busy={saving} onCancel={() => setAdding(false)} onSave={(b) => create(b)} />
                </Modal>
            )}
            {listName && (
                <Modal title="Save these filters as a list" onClose={() => setListName(null)}>
                    <Input label="List name" value={listName.name} onChange={(e) => setListName((x) => ({ ...x, name: e.target.value }))} />
                    <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={listName.shared}
                        onChange={(e) => setListName((x) => ({ ...x, shared: e.target.checked }))} />
                        Share with the office (each person still sees only their own contacts in it)</label>
                    <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setListName(null)}>Cancel</Button>
                        <Button disabled={!listName.name.trim()} onClick={saveList}>Save list</Button></div>
                </Modal>
            )}
        </div>
    );
};

// ---------------------------------------------------------------- pipeline
const PipelineTab = ({ meta, agents }) => {
    const api = useApiClient();
    const [type, setType] = useState('');
    const [agentId, setAgentId] = useState('');
    const [showClosed, setShowClosed] = useState(false);
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        const p = new URLSearchParams();
        if (type) p.set('type', type);
        if (agentId) p.set('agent_id', agentId);
        if (!showClosed) p.set('open', '1');
        api.get(`/api/crm/opportunities?${p}`).then(setRows).catch((e) => setError(e.message));
    }, [api, type, agentId, showClosed]);
    if (error) return <Notice message={error} />;
    if (!rows) return <Loading />;
    const stages = meta.stages.filter((s) => showClosed || meta.open_stages.includes(s.key));
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-end">
                <Select label="Type" value={type} onChange={(e) => setType(e.target.value)} className="w-44"
                    options={[{ value: '', label: 'All types' }, ...meta.opp_types.map((t) => ({ value: t, label: titleCase(t) }))]} />
                {meta.sees_everyone && (
                    <Select label="Agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="w-56"
                        options={[{ value: '', label: 'Whole office' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
                )}
                <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />Show closed and lost</label>
            </div>
            <p className="text-xs text-slate-500">Open a contact to move an opportunity. "Under contract" and "Closed" follow the linked transaction.</p>
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(14rem, 1fr))`, overflowX: 'auto' }}>
                {stages.map((s) => {
                    const list = rows.filter((o) => o.stage === s.key);
                    const total = list.reduce((t, o) => t + (Number(o.estimated_value) || 0), 0);
                    return (
                        <div key={s.key} className="bg-slate-50 border border-slate-200 rounded-lg p-2 min-h-[12rem]">
                            <div className="flex flex-wrap gap-3 justify-between items-center px-1 mb-2">
                                <Pill tone={STAGE_TONE[s.key]}>{s.label}</Pill>
                                <span className="text-xs text-slate-500">{list.length} · {money(total)}</span>
                            </div>
                            <div className="space-y-2">
                                {list.map((o) => (
                                    <Link key={o.opportunity_id} to={`/dashboard/crm/contacts/${o.contact_id}`}
                                        className="block bg-white border border-slate-200 rounded p-2 hover:shadow-sm">
                                        <div className="text-sm font-medium text-slate-800">{o.title || o.contact_name || o.company_name}</div>
                                        <div className="text-xs text-slate-500">{titleCase(o.opp_type)}{o.commercial_side ? ` · ${o.commercial_side}` : ''} · {o.contact_name || o.company_name}</div>
                                        <div className="text-xs mt-1 flex justify-between"><span>{money(o.estimated_value)}</span>
                                            <span className="text-slate-400">{o.expected_close_on ? fmtDate(o.expected_close_on) : ''}</span></div>
                                        {o.agent_name && meta.sees_everyone && <div className="text-[11px] text-slate-400">{o.agent_name}</div>}
                                        {o.transaction_suggests && <div className="text-[11px] text-amber-700 mt-1">Transaction says: {titleCase(o.transaction_suggests.to)}</div>}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------- follow-ups
const FollowUpsTab = ({ meta, agents }) => {
    const api = useApiClient();
    const [agentId, setAgentId] = useState('');
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(() => {
        api.get(`/api/crm/follow-ups${agentId ? `?agent_id=${agentId}` : ''}`).then(setD).catch((e) => setError(e.message));
    }, [api, agentId]);
    useEffect(() => { load(); }, [load]);
    const done = async (t) => {
        try { await api.put(`/api/crm/tasks/${t.task_id}`, { status: 'Completed' }); load(); } catch (e) { setError(e.message); }
    };
    if (error) return <Notice message={error} onDismiss={() => setError('')} />;
    if (!d) return <Loading />;
    const taskCols = [
        { key: 'due_date', label: 'Due', nowrap: true, render: (r) => fmtDate(r.due_date) },
        { key: 'title', label: 'Follow-up' },
        { key: 'contact', label: 'Contact', render: (r) => <Link className="text-indigo-700 hover:underline" to={`/dashboard/crm/contacts/${r.crm_contact_id}`}>{r.contact_name?.trim() || r.company_name}</Link> },
        ...(meta.sees_everyone ? [{ key: 'assigned_to_name', label: 'Agent' }] : []),
        { key: 'act', label: '', render: (r) => <Button variant="subtle" onClick={() => done(r)}>Done</Button> },
    ];
    return (
        <div className="space-y-6">
            {meta.sees_everyone && (
                <Select label="Agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="w-56"
                    options={[{ value: '', label: 'Whole office' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Overdue" value={d.overdue.length} tone={d.overdue.length ? 'bad' : 'good'} />
                <Stat label="Due today" value={d.today.length} tone={d.today.length ? 'warn' : 'default'} />
                <Stat label="This week" value={d.this_week.length} />
                <Stat label="No touch (past cadence)" value={d.no_touch_total} tone={d.no_touch_total ? 'warn' : 'good'} />
            </div>
            {meta.sees_everyone && !agentId && d.per_agent.length > 0 && (
                <Panel title="By agent">
                    <Table columns={[{ key: 'agent_name', label: 'Agent' }, { key: 'overdue', label: 'Overdue', align: 'right' },
                        { key: 'today', label: 'Today', align: 'right' }, { key: 'no_touch', label: 'No touch', align: 'right' }]}
                        rows={d.per_agent} />
                </Panel>
            )}
            <Panel title="Overdue follow-ups"><Table columns={taskCols} rows={d.overdue} rowTone={() => 'bad'} empty="Nothing overdue." /></Panel>
            <Panel title="Due today"><Table columns={taskCols} rows={d.today} empty="Nothing due today." /></Panel>
            <Panel title="Later this week"><Table columns={taskCols} rows={d.this_week} empty="Nothing else this week." /></Panel>
            <Panel title="No touch past their cadence" subtitle="Cadence: the card's own, or the relationship default in CRM settings">
                <Table columns={[
                    { key: 'display_name', label: 'Contact', render: (r) => <Link className="text-indigo-700 hover:underline" to={`/dashboard/crm/contacts/${r.contact_id}`}>{r.display_name}</Link> },
                    { key: 'category', label: 'Relationship', render: (r) => titleCase(r.category) },
                    { key: 'last', label: 'Last touch', render: (r) => (r.touch.last_touch ? `${fmtDate(r.touch.last_touch)} (${r.touch.days_since}d)` : 'never') },
                    { key: 'due', label: 'Was due', render: (r) => fmtDate(r.touch.due_on) },
                    { key: 'preferred_channel', label: 'Prefers', render: (r) => titleCase(r.preferred_channel) },
                    ...(meta.sees_everyone ? [{ key: 'agent_name', label: 'Agent' }] : []),
                ]} rows={d.no_touch} empty="Everyone is within their cadence." />
            </Panel>
        </div>
    );
};

// ---------------------------------------------------------------- lists
const ListsTab = ({ segments, reload, openList }) => {
    const api = useApiClient();
    const [error, setError] = useState('');
    const remove = async (s) => {
        if (!window.confirm(`Remove the list "${s.name}"? The contacts in it are not touched.`)) return;
        try { await api.delete(`/api/crm/segments/${s.segment_id}`); reload(); } catch (e) { setError(e.message); }
    };
    const describe = (f = {}) => Object.entries(f).map(([k, v]) => `${titleCase(k)}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ') || 'everyone';
    return (
        <Panel title="Saved lists" subtitle="A list is a saved filter, not a copy: it re-runs each time, inside each viewer's own book. Build one on the Contacts tab.">
            {error && <Notice message={error} onDismiss={() => setError('')} />}
            {segments.length === 0 ? <Empty title="No lists yet" message="Filter the Contacts tab, then choose Save as list." /> : (
                <Table columns={[
                    { key: 'name', label: 'List', render: (r) => <span className="font-medium">{r.name}</span> },
                    { key: 'filters', label: 'Filters', render: (r) => <span className="text-xs text-slate-600">{describe(r.filters)}</span> },
                    { key: 'owner', label: 'Owner', render: (r) => (r.mine ? 'me' : r.owner_name) },
                    { key: 'is_shared', label: 'Shared', render: (r) => (r.is_shared ? <Pill tone="indigo">shared</Pill> : '') },
                    { key: 'act', label: '', render: (r) => (
                        <span className="flex gap-2"><Button variant="subtle" onClick={() => openList(r.segment_id)}>Open</Button>
                            {r.mine && <Button variant="ghost" onClick={() => remove(r)}>Remove</Button>}</span>) },
                ]} rows={segments} />
            )}
        </Panel>
    );
};

// ---------------------------------------------------------------- import
const ImportTab = ({ meta, agents }) => {
    const api = useApiClient();
    const [file, setFile] = useState(null);
    const [opts, setOpts] = useState({ category: 'prospect', source: '', tags: '', on_duplicate: 'skip', assigned_agent_id: '' });
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [history, setHistory] = useState([]);
    const loadHistory = useCallback(() => api.get('/api/crm/imports').then(setHistory).catch(() => setHistory([])), [api]);
    useEffect(() => { loadHistory(); }, [loadHistory]);
    const run = async (dry) => {
        if (!file) return;
        setBusy(true); setError('');
        const fd = new FormData();
        fd.append('file', file);
        Object.entries(opts).forEach(([k, v]) => fd.append(k, v));
        fd.append('dry_run', dry ? '1' : '0');
        try { setReport(await api.post('/api/crm/import', fd, true)); if (!dry) loadHistory(); }
        catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const undo = async (b) => {
        if (!window.confirm('Archive the contacts this import created? Any that have been worked since are kept.')) return;
        try { const r = await api.post(`/api/crm/import/${b.batch_id}/undo`, {}); setError(''); window.alert(r.message); loadHistory(); }
        catch (e) { setError(e.message); }
    };
    const set = (k) => (e) => setOpts((x) => ({ ...x, [k]: e.target.value }));
    const lines = (list) => list.map((r) => ({ ...r, _key: r.line }));
    return (
        <div className="space-y-6">
            <Panel title="Import contacts from a spreadsheet" subtitle="CSV with a header row: First Name, Last Name (or Name), Email, Phone, Company, Address, City, State, Zip, Area, Source, Tags, Notes. Matched by email and phone against the whole firm's book; nothing is saved until you confirm.">
                {error && <Notice message={error} onDismiss={() => setError('')} />}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
                    <label className="block mb-3"><span className="text-xs font-medium text-slate-600">CSV file</span>
                        <input type="file" accept=".csv,text/csv" className="mt-1 block text-sm" onChange={(e) => { setFile(e.target.files[0] || null); setReport(null); }} /></label>
                    <Select label="Relationship (when the file has none)" value={opts.category} onChange={set('category')} options={meta.categories} />
                    <Input label="Source" value={opts.source} onChange={set('source')} placeholder="e.g. Spring open house" />
                    <Input label="Add tags" value={opts.tags} onChange={set('tags')} />
                    <Select label="When someone is already in my book" value={opts.on_duplicate} onChange={set('on_duplicate')}
                        options={[{ value: 'skip', label: 'Skip them' }, { value: 'fill_blanks', label: 'Fill in blanks and add tags (never overwrite)' }]} />
                    {meta.sees_everyone && (
                        <Select label="Assign to" value={opts.assigned_agent_id} onChange={set('assigned_agent_id')}
                            options={[{ value: '', label: 'Me / office pool' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
                    )}
                </div>
                <div className="flex gap-2">
                    <Button variant="ghost" disabled={!file || busy} onClick={() => run(true)}>Preview</Button>
                    <Button disabled={!file || busy || !report || !report.dry_run || !report.create} onClick={() => run(false)}>
                        {report?.dry_run ? `Import ${report.create} contact(s)` : 'Import'}</Button>
                </div>
                <p className="text-xs text-slate-500 mt-3">Importing a list is not consent to call it. Every call, text and email is checked against the do-not-call registry and the brokerage's own list at the moment it is made.</p>
            </Panel>
            {report && (
                <Panel title={report.dry_run ? 'Preview — nothing saved yet' : report.message}>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <Stat label="New" value={report.create} tone="good" />
                        <Stat label="Already in my book" value={report.duplicate - report.duplicate_elsewhere} />
                        <Stat label="With another agent" value={report.duplicate_elsewhere} tone={report.duplicate_elsewhere ? 'warn' : 'default'} />
                        <Stat label="Repeated in the file" value={report.duplicate_in_file} />
                        <Stat label="Unusable rows" value={report.invalid} tone={report.invalid ? 'bad' : 'default'} />
                    </div>
                    {report.unknown_columns?.length > 0 && <Notice tone="warn" message={`Columns ignored: ${report.unknown_columns.join(', ')}`} />}
                    {report.details.invalid.length > 0 && (
                        <><h3 className="text-sm font-semibold mb-1">Unusable rows</h3>
                            <Table columns={[{ key: 'line', label: 'Line' }, { key: 'name', label: 'Name' }, { key: 'errors', label: 'Problem', render: (r) => r.errors.join('; ') }]}
                                rows={lines(report.details.invalid)} /></>
                    )}
                    {report.details.duplicate.length > 0 && (
                        <><h3 className="text-sm font-semibold mt-4 mb-1">Already in the book</h3>
                            <Table columns={[{ key: 'line', label: 'Line' }, { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
                                { key: 'match', label: 'Matches', render: (r) => (r.match_contact_id
                                    ? <Link className="text-indigo-700 hover:underline" to={`/dashboard/crm/contacts/${r.match_contact_id}`}>your card</Link>
                                    : <span className="text-amber-700">another agent's contact — ask the office</span>) }]}
                                rows={lines(report.details.duplicate)} /></>
                    )}
                    {report.details.create.some((r) => r.warnings.length) && (
                        <><h3 className="text-sm font-semibold mt-4 mb-1">Imported with a warning</h3>
                            <Table columns={[{ key: 'line', label: 'Line' }, { key: 'name', label: 'Name' }, { key: 'warnings', label: 'Warning', render: (r) => r.warnings.join('; ') }]}
                                rows={lines(report.details.create.filter((r) => r.warnings.length))} /></>
                    )}
                </Panel>
            )}
            <Panel title="Recent imports">
                <Table columns={[
                    { key: 'created_at', label: 'When', render: (r) => fmtDate(r.created_at) },
                    { key: 'filename', label: 'File' }, { key: 'username', label: 'By' },
                    { key: 'rows_created', label: 'Created', align: 'right' }, { key: 'rows_updated', label: 'Filled', align: 'right' },
                    { key: 'rows_duplicate', label: 'Duplicates', align: 'right' }, { key: 'rows_invalid', label: 'Invalid', align: 'right' },
                    { key: 'act', label: '', render: (r) => (r.undone_at ? <Pill>undone</Pill> : <Button variant="ghost" onClick={() => undo(r)}>Undo</Button>) },
                ]} rows={history} empty="No imports yet." />
            </Panel>
        </div>
    );
};

// ---------------------------------------------------------------- settings
const SettingsModal = ({ meta, onClose, onSaved }) => {
    const api = useApiClient();
    const s = meta.settings || {};
    const [f, setF] = useState({
        auto_follow_up_task: !!s.auto_follow_up_task, follow_up_days: s.follow_up_days || 3,
        follow_transaction_status: !!s.follow_transaction_status,
        cadence: Object.fromEntries(meta.categories.map((c) => [c,
            s.cadence && Object.prototype.hasOwnProperty.call(s.cadence, c) ? (s.cadence[c] ?? '') : (meta.default_cadence[c] ?? '')])),
    });
    const [error, setError] = useState('');
    const save = async () => {
        try { await api.put('/api/crm/settings', f); onSaved(); } catch (e) { setError(e.message); }
    };
    return (
        <Modal title="CRM settings" subtitle="Every automation here is off until you switch it on. Nothing is ever emailed or texted by the CRM." onClose={onClose} wide>
            {error && <Notice message={error} />}
            <label className="flex items-start gap-2 text-sm mb-2"><input type="checkbox" className="mt-1" checked={f.auto_follow_up_task}
                onChange={(e) => setF((x) => ({ ...x, auto_follow_up_task: e.target.checked }))} />
                When a call, email, text or meeting is logged, open a follow-up task for the agent (a task in the portal — nothing is sent to the contact)</label>
            <Input label="…due this many days later (weekends roll to Monday)" type="number" min="1" max="90" value={f.follow_up_days}
                onChange={(e) => setF((x) => ({ ...x, follow_up_days: e.target.value }))} className="max-w-xs" />
            <label className="flex items-start gap-2 text-sm mb-4"><input type="checkbox" className="mt-1" checked={f.follow_transaction_status}
                onChange={(e) => setF((x) => ({ ...x, follow_transaction_status: e.target.checked }))} />
                Move a linked opportunity's stage when its transaction's status changes (off = the screen shows the difference and the agent confirms)</label>
            <h3 className="text-sm font-semibold mb-1">Keep-in-touch cadence (days; blank = none)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3">
                {meta.categories.map((c) => (
                    <Input key={c} label={titleCase(c)} type="number" min="1" value={f.cadence[c]}
                        onChange={(e) => setF((x) => ({ ...x, cadence: { ...x.cadence, [c]: e.target.value } }))} />
                ))}
            </div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save}>Save</Button></div>
        </Modal>
    );
};

// ---------------------------------------------------------------- page
const TABS = ['dashboard', 'contacts', 'pipeline', 'follow-ups', 'lists', 'import'];

const CrmPage = () => {
    const api = useApiClient();
    const { meta, error, reload } = useMeta();
    const [params, setParams] = useSearchParams();
    const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'dashboard';
    const [segments, setSegments] = useState([]);
    const [openSegment, setOpenSegment] = useState('');
    const [settings, setSettings] = useState(false);
    const agents = useAgents(!!meta?.sees_everyone);
    const loadSegments = useCallback(() => api.get('/api/crm/segments').then(setSegments).catch(() => setSegments([])), [api]);
    useEffect(() => { loadSegments(); }, [loadSegments]);
    const go = (t) => setParams({ tab: t });

    if (error) return <div className="p-8"><Notice message={error} /></div>;
    if (!meta) return <Loading />;
    const j = meta.jurisdiction;
    return (
        <div>
            <PageHeader title="CRM" subtitle={`Contacts, pipeline and follow-ups — ${j.name}.`}
                secondary={meta.can_edit_settings ? <Button variant="ghost" onClick={() => setSettings(true)}>CRM settings</Button> : null} />
            <AboutThisPage>
                <p>Calls, texts and emails start from a contact's card, after the do-not-call check; nothing is sent automatically.</p>
            </AboutThisPage>
            {j.crm?.verify && <Notice tone="warn" title={`${j.name} telephone-solicitation rules`} message={`${j.crm.note} Calling hours ${j.crm.calling_hours.start}:00–${j.crm.calling_hours.end}:00 are enforced on this site.`} />}
            <Tabs active={tab} onChange={go} tabs={[
                { key: 'dashboard', label: 'Dashboard' }, { key: 'contacts', label: 'Contacts' }, { key: 'pipeline', label: 'Pipeline' },
                { key: 'follow-ups', label: 'Follow-ups' }, { key: 'lists', label: 'Lists', count: segments.length }, { key: 'import', label: 'Import' },
            ]} />
            {tab === 'dashboard' && <DashboardTab meta={meta} agents={agents} />}
            {tab === 'contacts' && <ContactsTab meta={meta} agents={agents} segments={segments} reloadSegments={loadSegments} initialSegment={openSegment} />}
            {tab === 'pipeline' && <PipelineTab meta={meta} agents={agents} />}
            {tab === 'follow-ups' && <FollowUpsTab meta={meta} agents={agents} />}
            {tab === 'lists' && <ListsTab segments={segments} reload={loadSegments} openList={(id) => { setOpenSegment(String(id)); go('contacts'); }} />}
            {tab === 'import' && <ImportTab meta={meta} agents={agents} />}
            {settings && <SettingsModal meta={meta} onClose={() => setSettings(false)} onSaved={() => { setSettings(false); reload(); }} />}
        </div>
    );
};

export default CrmPage;
